import { Dispatch, SetStateAction, useCallback, useEffect, useReducer, useState } from 'react';
import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import {
  deleteUnrestrictedAccount,
  getUnrestrictedAccounts,
} from '../../../api/firmsApi/unrestrictedAccountsApi';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import Popup from '../../../components/Popup';
import TableContainer from '../../../components/Table/TableContainer';
import { POPUP_DESCRIPTIONS } from '../../../constants/popupDescriptions';
import { FIRM_BANKING_UNRESTRICTED_ACCOUNTS_LIST_TABLE_LABELS } from '../../../constants/tableLabels';
import { handleSortTableData } from '../../../helpers/handleSortTableData';
import useSnackBar from '../../../hooks/useSnackBar';
import usePagination from '../../../hooks/usePagination';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { ActiveSortField, SelectOption, UnrestrictedAccountShort } from '../../../types';
import FirmBankingAddAccountButton from '../FirmBankingAddAccountButton';
import { MenuOption } from '../types';

import FirmUnrestrictedAccountTableRow from './FirmUnrestrictedAccountTableRow';
import FirmUnrestrictedAccountPopup, { Mode } from './FirmUnrestrictedAccountPopup';
import FirmUnrestrictedAccountDetailsPopup from './FirmUnrestrictedAccountDetailsPopup';
import { initialState, reducer } from './reducer';

const useStyles = makeStyles()((theme: Theme) => ({
  systemAccountsLabel: {
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '120%',
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  tableHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '12px',

    '@media (max-width: 800px)': {
      flexDirection: 'column',
      maxHeight: 'auto',
      gap: '12px',
    },
  },
}));

interface FirmUnrestrictedAccountsViewProps {
  setShortUnrestrictedAccountData: Dispatch<SetStateAction<SelectOption[]>>;
}

// TODO: implement a common component for trust & unrestricted accounts Views, etc.
const FirmUnrestrictedAccountsView = ({
  setShortUnrestrictedAccountData,
}: FirmUnrestrictedAccountsViewProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const canCreate = useHasPermission('Banking', 'Create');
  const canEdit = useHasPermission('Banking', 'Edit');
  const canDelete = useHasPermission('Banking', 'Delete');

  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);

  const sortableFields = {
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  const fetchUnrestrictedAccounts = useCallback(() => {
    dispatch({ type: 'SET_IS_LOADING', isLoading: true });

    getUnrestrictedAccounts({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      activeSortField,
    })
      .then(({ data }) => {
        if (data.totalCount !== null) {
          setTotalCount && setTotalCount(data.totalCount);
        }

        const parsedShortData = data.resultSet.map((resultItem) => ({
          id: String(resultItem.id),
          label: resultItem.name,
        }));

        dispatch({ type: 'SET_UNRESTRICTED_ACCOUNTS', unrestrictedAccounts: data.resultSet });
        setShortUnrestrictedAccountData(parsedShortData);
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message || 'Cannot get unrestricted accounts.',
          type: 'error',
        }));
      })
      .finally(() => {
        dispatch({ type: 'SET_IS_LOADING', isLoading: false });
      });
  }, [pageNumber, rowsPerPage, activeSortField]);

  const handleDeleteUnrestrictedAccount = () => {
    if (!state.selectedUnrestrictedAccount) {
      return;
    }

    dispatch({ type: 'CLOSE_POPUP' });

    deleteUnrestrictedAccount(state.selectedUnrestrictedAccount.id)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Unrestricted account deleted successfully.',
          type: 'success',
        }));
        fetchUnrestrictedAccounts();
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message: error.response?.data.message,
          type: 'error',
        }));
      });
  };

  const handleSuccess = () => {
    handleReset();
    fetchUnrestrictedAccounts();
  };

  const handleReset = () => {
    dispatch({ type: 'CLOSE_POPUP' });
  };

  const handleOptionClick = (accountId: number, option: MenuOption) => {
    const account = state.unrestrictedAccounts.find(
      (el) => el.id === accountId,
    ) as UnrestrictedAccountShort;

    switch (option) {
      case MenuOption.Details:
        dispatch({ type: 'OPEN_DETAILS_POPUP', unrestrictedAccount: account });
        break;
      case MenuOption.Edit:
        dispatch({
          type: 'OPEN_UNRESTRICTED_ACCOUNT_POPUP',
          mode: Mode.Edit,
          unrestrictedAccount: account,
        });
        break;
    }
  };

  const handleDeleteClick = (accountId: number) => {
    const account = state.unrestrictedAccounts.find(
      (el) => el.id === accountId,
    ) as UnrestrictedAccountShort;

    dispatch({ type: 'OPEN_DELETE_POPUP', unrestrictedAccount: account });
  };

  useEffect(() => {
    fetchUnrestrictedAccounts();
  }, [activeSortField, pageNumber, rowsPerPage]);

  return (
    <Box mt="40px">
      <Box className={classes.tableHeadline}>
        <Typography className={classes.systemAccountsLabel}>Unrestricted Accounts</Typography>
        {canCreate && (
          <FirmBankingAddAccountButton
            buttonText="Add Unrestricted Account"
            handleClick={() =>
              dispatch({ type: 'OPEN_UNRESTRICTED_ACCOUNT_POPUP', mode: Mode.Create })
            }
          />
        )}
      </Box>

      {state.isLoading ? (
        <Loader colorType="warning"></Loader>
      ) : (
        <>
          {state.unrestrictedAccounts.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={FIRM_BANKING_UNRESTRICTED_ACCOUNTS_LIST_TABLE_LABELS}
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
            >
              <FirmUnrestrictedAccountTableRow
                data={state.unrestrictedAccounts}
                onEditButtonClick={handleOptionClick}
                onDeleteButtonClick={handleDeleteClick}
                canDelete={canDelete}
              />
            </TableContainer>
          )}

          <FirmUnrestrictedAccountPopup
            isOpen={state.unrestrictedAccountPopup.isOpen}
            mode={state.unrestrictedAccountPopup.mode}
            data={state.selectedUnrestrictedAccount}
            canEdit={canEdit}
            onSuccess={handleSuccess}
            onCancel={handleReset}
          />

          <FirmUnrestrictedAccountDetailsPopup
            isOpen={state.isDetailsPopupOpen}
            accountId={state.selectedUnrestrictedAccount?.id as number}
            onClose={handleReset}
          />

          <Popup
            isOpen={state.isDeletePopupOpen}
            headlineText={POPUP_DESCRIPTIONS.unrestrictedAccountDelete.headlineText}
            contentText={POPUP_DESCRIPTIONS.unrestrictedAccountDelete.contentText}
            onMainButtonClick={handleDeleteUnrestrictedAccount}
            onSecondaryButtonClick={handleReset}
          />
        </>
      )}
    </Box>
  );
};

export default FirmUnrestrictedAccountsView;
